import type {
  CreateOrganizationUserParams,
  CreateOrganizationUserWithAdvisorCodesParams,
  ListOrganizationUsersParams,
  OrganizationInfo,
  OrganizationUserParams,
  UpdateOrganizationUserParams,
  UpdateOrganizationUserWithAdvisorCodesParams,
} from '@/types/api/organizations'
import type {
  AdvisorCodesBatch,
  AdvisorCodesBatchRequest,
  CreateUserResponse,
  GetAdvisorCodesResponse,
  ListUsersResponse,
  User,
} from '@/types/api/roles'
import type {
  AccountCountsResponse,
  GetAccountCountsParams,
  GetUserAccessParams,
  GetUserAccessResponse,
  SelfResponse,
} from '@/types/api/users'

import { TagTypes } from '@/types/api/tag-types'

import { baseApiSlice } from './base-api-slice'

export const userSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get current user profile
    getSelf: builder.query<SelfResponse, void>({
      query: () => ({
        url: 'user/self/',
        method: 'GET',
      }),
      providesTags: [TagTypes.User],
    }),

    // Patch current user's own profile (no permissions required)
    patchSelf: builder.mutation<
      SelfResponse,
      { first_name?: string; last_name?: string }
    >({
      query: (updates) => ({
        url: 'user/self/',
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: [TagTypes.User],
    }),

    // List users in organization
    listOrganizationUsers: builder.query<
      ListUsersResponse,
      ListOrganizationUsersParams
    >({
      query: ({
        organizationId,
        status,
        filter_org_id,
        page,
        limit,
        search,
      }) => ({
        url: `org/${organizationId}/user/`,
        method: 'GET',
        params: {
          ...(status && { status }),
          ...(filter_org_id && { org_id: filter_org_id }),
          ...(page && { page }),
          ...(limit && { limit }),
          ...(search && { search }),
        },
      }),
      providesTags: [TagTypes.User],
    }),

    // Create new user
    createOrganizationUser: builder.mutation<
      User,
      CreateOrganizationUserParams
    >({
      query: ({ organization_id, user }) => ({
        url: `org/${organization_id}/user/`,
        method: 'POST',
        body: user,
      }),
      invalidatesTags: [TagTypes.User],
    }),

    // Create new user with advisor codes
    createOrganizationUserWithAdvisorCodes: builder.mutation<
      CreateUserResponse,
      CreateOrganizationUserWithAdvisorCodesParams
    >({
      query: ({ organization_id, user }) => ({
        url: `account/organizations/${organization_id}/users/`,
        method: 'POST',
        body: user,
      }),
      invalidatesTags: [TagTypes.User],
    }),

    // https://github.com/moment-technology/atlas/blob/main/atlas-openapi.yaml#L497
    getOrganizationUser: builder.query<
      OrganizationInfo,
      OrganizationUserParams
    >({
      query: ({ organization_id, user_id }) => ({
        url: `org/${organization_id}/user/${user_id}/`,
        method: 'GET',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, arg) =>
        result ? [{ type: TagTypes.User, id: arg.user_id }] : [TagTypes.User],
    }),

    // Get user by user id
    getUserById: builder.query<User, { userId: string }>({
      query: ({ userId }) => ({
        url: `user/${userId}/`,
        method: 'GET',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, arg) =>
        result ? [{ type: TagTypes.User, id: arg.userId }] : [TagTypes.User],
    }),

    // Update user
    updateOrganizationUser: builder.mutation<
      User,
      UpdateOrganizationUserParams
    >({
      query: ({ organization_id, user_id, updates }) => ({
        url: `org/${organization_id}/user/${user_id}/`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: [TagTypes.User],
    }),

    updateOrganizationUserWithAdvisorCodes: builder.mutation<
      User,
      UpdateOrganizationUserWithAdvisorCodesParams
    >({
      query: ({ organization_id, user_id, updates }) => ({
        url: `account/organizations/${organization_id}/users/${user_id}/`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: [TagTypes.User],
    }),

    // Partially update user
    patchOrganizationUser: builder.mutation<User, UpdateOrganizationUserParams>(
      {
        query: ({ organization_id, user_id, updates }) => ({
          url: `org/${organization_id}/user/${user_id}/`,
          method: 'PATCH',
          body: updates,
        }),
        invalidatesTags: [TagTypes.User],
      },
    ),

    // Delete user from organization
    deactivateOrganizationUser: builder.mutation<void, OrganizationUserParams>({
      query: ({ organization_id, user_id }) => ({
        url: `org/${organization_id}/user/${user_id}/deactivate/`,
        method: 'PUT',
      }),
      invalidatesTags: [TagTypes.User],
    }),

    // Reactivate user in organization
    reactivateOrganizationUser: builder.mutation<void, OrganizationUserParams>({
      query: ({ organization_id, user_id }) => ({
        url: `org/${organization_id}/user/${user_id}/reactivate/`,
        method: 'PUT',
      }),
      invalidatesTags: [TagTypes.User],
    }),

    // Get account counts for users
    getAccountCounts: builder.mutation<
      AccountCountsResponse,
      GetAccountCountsParams
    >({
      query: ({ organization_id, user_ids }) => ({
        url: `account/organizations/${organization_id}/users/account-counts/`,
        method: 'POST',
        body: { user_ids },
      }),
    }),

    // Get advisor codes for multiple users
    getAdvisorCodesBatch: builder.mutation<
      AdvisorCodesBatch,
      { organization_id: string } & AdvisorCodesBatchRequest
    >({
      query: ({ organization_id, user_ids }) => ({
        url: `account/organizations/${organization_id}/users/advisor-codes/`,
        method: 'POST',
        body: { user_ids },
      }),
    }),

    // Get user access
    getUserAccess: builder.query<GetUserAccessResponse, GetUserAccessParams>({
      query: ({ organization_id, user_id }) => ({
        url: `account/organizations/${organization_id}/users/${user_id}/access/`,
        method: 'GET',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, arg) =>
        result ? [{ type: TagTypes.User, id: arg.user_id }] : [TagTypes.User],
    }),

    // Get advisor codes for a single user
    getAdvisorCodes: builder.query<
      GetAdvisorCodesResponse,
      { organization_id: string; user_id: string }
    >({
      query: ({ organization_id, user_id }) => ({
        url: `account/organizations/${organization_id}/users/${user_id}/advisor-codes/`,
        method: 'GET',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, arg) =>
        result ? [{ type: TagTypes.User, id: arg.user_id }] : [TagTypes.User],
    }),
  }),
})

export const {
  endpoints: {
    getSelf,
    patchSelf,
    listOrganizationUsers,
    createOrganizationUser,
    createOrganizationUserWithAdvisorCodes,
    getOrganizationUser,
    getUserById,
    updateOrganizationUser,
    patchOrganizationUser,
    deactivateOrganizationUser,
    reactivateOrganizationUser,
    getAccountCounts,
    getAdvisorCodesBatch,
    getUserAccess,
    getAdvisorCodes,
  },
  useGetSelfQuery,
  usePatchSelfMutation,
  useListOrganizationUsersQuery,
  useCreateOrganizationUserMutation,
  useCreateOrganizationUserWithAdvisorCodesMutation,

  useGetUserByIdQuery,
  useUpdateOrganizationUserMutation,
  useUpdateOrganizationUserWithAdvisorCodesMutation,
  usePatchOrganizationUserMutation,

  useDeactivateOrganizationUserMutation,
  useReactivateOrganizationUserMutation,

  useGetUserAccessQuery,
  useGetAdvisorCodesQuery,
} = userSlice
